import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, 
  Edit3, 
  CheckCircle2, 
  Truck, 
  Calendar, 
  User, 
  Clock, 
  Settings2,
  Info,
  ShieldCheck,
  Package,
  History
} from 'lucide-react';
import { useEquipmentRequestsDetail } from './hooks/useEquipmentRequestsDetail';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { cn } from '@/utils/cn';

export function EquipmentRequestDetailPage() {
  const navigate = useNavigate();
  const { request, isLoading, isApproving, approve } = useEquipmentRequestsDetail();
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-5xl animate-pulse space-y-4">
        <div className="h-20 rounded-md bg-white border border-slate-100" />
        <div className="grid gap-4 md:grid-cols-12">
          <div className="md:col-span-8 h-96 rounded-md bg-white border border-slate-100" />
          <div className="md:col-span-4 h-64 rounded-md bg-white border border-slate-100" />
        </div>
      </div>
    );
  }

  if (!request) return <div className="p-10 text-center text-slate-400 font-normal">Không tìm thấy yêu cầu</div>;

  const statusMap = {
    requested: { label: 'Chờ duyệt', color: 'bg-amber-500', text: 'text-amber-600', bg: 'bg-amber-50' },
    approved: { label: 'Đã duyệt', color: 'bg-blue-500', text: 'text-blue-600', bg: 'bg-blue-50' },
    in_use: { label: 'Đang dùng', color: 'bg-emerald-500', text: 'text-emerald-600', bg: 'bg-emerald-50' },
    returned: { label: 'Đã trả', color: 'bg-slate-400', text: 'text-slate-500', bg: 'bg-slate-50' },
    rejected: { label: 'Từ chối', color: 'bg-red-500', text: 'text-red-600', bg: 'bg-red-50' },
  };

  const priorityMap = {
    low: { label: 'Thấp', color: 'bg-slate-100 text-slate-500' },
    normal: { label: 'Thường', color: 'bg-slate-100 text-slate-700' },
    high: { label: 'Cao', color: 'bg-orange-50 text-orange-600' },
    urgent: { label: 'Khẩn', color: 'bg-red-50 text-red-600' },
  };

  return (
    <div className="mx-auto max-w-5xl space-y-4 pb-12">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 rounded-md border border-slate-100 bg-white p-3">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => navigate('/equipment/requests')}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 active:scale-95 transition-all"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-normal text-slate-900 truncate tracking-tight">{request.title}</h1>
              <span className={cn('px-2 py-0.5 rounded text-[10px] font-normal  whitespace-nowrap', statusMap[request.status_code as keyof typeof statusMap].bg, statusMap[request.status_code as keyof typeof statusMap].text)}>
                {statusMap[request.status_code as keyof typeof statusMap].label}
              </span>
            </div>
            <p className="sm:text-sm text-xs text-slate-500 font-normal">EREQ-{request.id.substring(0, 8)} · {request.project?.name || '-'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {request.status_code === 'requested' && (
            <button
              onClick={() => setConfirmOpen(true)}
              className="flex h-10 items-center gap-2 rounded-md bg-slate-900 px-4 sm:text-sm text-xs font-normal text-white -slate-900/10 hover:bg-slate-800 transition-all active:scale-95"
            >
              <CheckCircle2 className="h-4 w-4" />
              <span className="hidden sm:inline">Duyệt yêu cầu</span>
            </button>
          )}
          <button
             onClick={() => navigate(`/equipment/requests/edit/${request.id}`)}
             className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 active:scale-95 transition-all"
          >
            <Edit3 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-12">
        {/* Main Details */}
        <div className="md:col-span-8 space-y-4">
          <section className="rounded-md border border-slate-100 bg-white p-4 space-y-6">
            <div className="flex items-center gap-2 border-b border-slate-50 pb-2 text-slate-800 font-normal">
              <Info className="h-4 w-4 text-slate-900" />
              <span>Nội dung đề xuất</span>
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <div className="space-y-1">
                <span className="text-[10px] font-normal text-slate-400 tracking-widest">Dự án tiếp nhận</span>
                <p className="sm:text-sm text-xs font-normal text-slate-700">{request.project?.name || '-'}</p>
              </div>
              <div className="space-y-1">
                <span className="text-[10px] font-normal text-slate-400 tracking-widest">Độ khẩn cấp</span>
                <div>
                   <span className={cn('px-2 py-0.5 rounded text-[10px] font-normal ', priorityMap[request.priority as keyof typeof priorityMap].color)}>
                    {priorityMap[request.priority as keyof typeof priorityMap].label}
                  </span>
                </div>
              </div>
              <div className="space-y-1">
                <span className="text-[10px] font-normal text-slate-400 tracking-widest">Thời gian dự kiến</span>
                <div className="flex items-center gap-2 sm:text-sm text-xs font-normal text-slate-700">
                   <Calendar className="h-4 w-4 text-slate-400" />
                   <span>{new Date(request.needed_from).toLocaleDateString('vi-VN')}</span>
                   {request.needed_until && <span className="text-slate-300">→ {new Date(request.needed_until).toLocaleDateString('vi-VN')}</span>}
                </div>
              </div>
              <div className="space-y-1">
                <span className="text-[10px] font-normal text-slate-400 tracking-widest">Người yêu cầu</span>
                <div className="flex items-center gap-2">
                   <div className="h-5 w-5 rounded-full bg-slate-100 border border-slate-200" />
                   <p className="sm:text-sm text-xs font-normal text-slate-700">{request.requester?.full_name || '-'}</p>
                </div>
              </div>
              <div className="sm:col-span-2 space-y-2">
                <span className="text-[10px] font-normal text-slate-400 tracking-widest">Ghi chú mục đích</span>
                <p className="sm:text-sm text-xs text-slate-600 leading-relaxed bg-slate-50/50 p-3 rounded-md border border-slate-100 border-dashed">
                  {request.description || 'Không có mô tả chi tiết.'}
                </p>
              </div>
            </div>
          </section>

          {/* Equipment Items Table */}
          <section className="rounded-md border border-slate-100 bg-white overflow-hidden">
            <div className="flex items-center justify-between border-b border-slate-100 p-4 font-normal text-slate-800">
              <div className="flex items-center gap-2">
                <Settings2 className="h-4 w-4 text-slate-900" />
                <span>Thiết bị yêu cầu</span>
              </div>
            </div>
            <table className="w-full text-left sm:text-sm text-xs">
              <thead>
                <tr className="border-b border-slate-50 bg-slate-50/30 text-[10px] font-normal text-slate-400 tracking-wider">
                  <th className="p-4 font-normal">Loại thiết bị</th>
                  <th className="p-4 text-right font-normal">Số lượng</th>
                  <th className="p-4 font-normal">Thiết bị được chỉ định</th>
                  <th className="p-4 font-normal">Ghi chú</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {request.items?.map((item: any) => (
                  <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="p-4">
                      <p className="font-normal text-slate-800">{item.equipment_type?.name || 'Vật tư/Máy móc'}</p>
                    </td>
                    <td className="p-4 text-right font-normal text-slate-900">
                      {item.quantity} chiếc
                    </td>
                    <td className="p-4">
                       {item.equipment ? (
                         <div className="flex items-center gap-2">
                           <div className="h-2 w-2 rounded-full bg-emerald-500" />
                           <span className="sm:text-sm text-xs font-normal text-slate-700">[{item.equipment.code}] {item.equipment.name}</span>
                         </div>
                       ) : (
                         <span className="text-[10px] font-normal text-slate-300 italic">Chưa chỉ định</span>
                       )}
                    </td>
                    <td className="p-4 sm:text-sm text-xs text-slate-500 italic">
                      {item.note || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>

        {/* Sidebar Status */}
        <div className="md:col-span-4 space-y-4">
          <section className="rounded-md border border-slate-100 bg-white p-4 space-y-4">
             <h3 className="text-[10px] font-normal text-slate-400 tracking-widest border-b border-slate-50 pb-2">Trạng thái phê duyệt</h3>
             <div className="space-y-3">
                <div className="flex items-center justify-between">
                   <span className="sm:text-sm text-xs font-normal text-slate-500">Trạng thái</span>
                    <span className={cn('px-2 py-0.5 rounded text-[10px] font-normal ', statusMap[request.status_code as keyof typeof statusMap].bg, statusMap[request.status_code as keyof typeof statusMap].text)}>
                      {statusMap[request.status_code as keyof typeof statusMap].label}
                    </span>
                </div>
                <div className="flex items-center justify-between">
                   <span className="sm:text-sm text-xs font-normal text-slate-500">Người phê duyệt</span>
                   <span className="sm:text-sm text-xs font-normal text-slate-700">{request.approver?.full_name || '---'}</span>
                </div>
                {request.approved_at && (
                  <div className="flex items-center justify-between border-t border-slate-50 pt-2">
                     <span className="sm:text-sm text-xs font-normal text-slate-500">Ngày phê duyệt</span>
                     <span className="sm:text-sm text-xs font-normal text-slate-700">{new Date(request.approved_at).toLocaleDateString('vi-VN')}</span>
                  </div>
                )}
             </div>
          </section>

          <section className="rounded-md border border-slate-100 bg-white p-4 space-y-4">
             <h3 className="text-[10px] font-normal text-slate-400 tracking-widest border-b border-slate-50 pb-2">Dòng thời gian</h3>
             <div className="space-y-4">
                <div className="flex gap-3">
                   <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-slate-900 -[0_0_8px_rgba(15,23,42,0.3)]" />
                   <div className="space-y-1">
                      <p className="text-[11px] font-normal text-slate-700">Khởi tạo yêu cầu</p>
                      <p className="text-[10px] text-slate-400 font-normal">{new Date(request.created_at).toLocaleString('vi-VN')}</p>
                   </div>
                </div>
                {request.status_code === 'approved' && (
                  <div className="flex gap-3">
                     <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-blue-500 -[0_0_8px_rgba(59,130,246,0.3)]" />
                     <div className="space-y-1">
                        <p className="text-[11px] font-normal text-slate-700">Phê duyệt & Chỉ định máy</p>
                        <p className="text-[10px] text-slate-400 font-normal">{new Date(request.approved_at).toLocaleString('vi-VN')}</p>
                     </div>
                  </div>
                )}
             </div>
          </section>

          <div className="rounded-md bg-slate-900 p-6 text-center text-white -slate-900/20">
             <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm">
                <ShieldCheck className="h-6 w-6" />
             </div>
             <p className="sm:text-sm text-xs font-normal tracking-widest mb-1">Dữ liệu bảo mật</p>
             <p className="text-[10px] text-slate-400 leading-relaxed font-normal">Hệ thống ghi nhận mọi thay đổi và phê duyệt theo tiêu chuẩn ISO 9001:2015.</p>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Xác nhận phê duyệt"
        description={`Bạn có chắc chắn muốn phê duyệt yêu cầu "${request.title}"? Thao tác này sẽ cho phép điều động thiết bị đến dự án.`}
        confirmLabel="Phê duyệt"
        variant="primary"
        onConfirm={() => {
          approve();
          setConfirmOpen(false);
        }}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
}
